import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Typography from '@/components/base/typography'
import { whereToFindUsRevealAnimation } from '@/components/sections/animations/where-to-find-us'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'

import { WPImage, WPLink } from '@/types'

type WhereToFindUsProps = {
  dark?: boolean
  type?: 'big' | 'small'
} & HTMLAttributes<HTMLDivElement>

type BannerContent = {
  title?: string
  text?: string
  image?: WPImage
  link1?: WPLink
}

const WhereToFindUs = ({ type = 'small', className }: WhereToFindUsProps) => {
  const { strings } = useStore()

  const where = (strings?.footer?.where || {}) as BannerContent

  const renderMobile = () => {
    return (
      <Container className="block md:hidden">
        <Spacer variant="xxl" />
        <Grid className="gap-[30px]">
          <div className="col-span-2 flex flex-col gap-spacing-lg">
            <Typography variant="h1" component="h2" data-motion-title>
              {where?.title}
            </Typography>
            <Typography variant="regular" data-motion-text>
              {where?.text}
            </Typography>
            <CtaButton
              href={where?.link1?.url}
              target={where?.link1?.target}
              variant="primary"
              className="place-self-start"
              data-motion-cta
            >
              {where?.link1?.title}
            </CtaButton>
          </div>
          <div className="relative col-span-2 aspect-square">
            <div className="absolute inset-0 h-full w-full" data-motion-media>
              <img
                src={where?.image?.url}
                alt={where?.image?.alt || ''}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </Grid>
        <Spacer variant="xxl" />
      </Container>
    )
  }

  const renderDesktopSmall = () => {
    return (
      <Container className="hidden md:block">
        <Spacer variant="xxl" />
        <Grid>
          <div className="flex flex-col gap-spacing-lg md:col-span-3 lg:col-span-4">
            <Typography variant="h1" component="h2" data-motion-title>
              {where?.title}
            </Typography>
            <Typography variant="regular" data-motion-text>
              {where?.text}
            </Typography>
            <CtaButton
              href={where?.link1?.url}
              target={where?.link1?.target}
              variant="primary"
              className="place-self-start"
              data-motion-cta
            >
              {where?.link1?.title}
            </CtaButton>
          </div>
          <div className="relative h-full md:col-span-3 lg:col-span-6 lg:col-start-7">
            <div className="absolute inset-0 h-full w-full" data-motion-media>
              <img
                src={where?.image?.url}
                alt={where?.image?.alt || ''}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </Grid>
        <Spacer variant="xxl" />
      </Container>
    )
  }

  const renderDesktopBig = () => {
    return (
      <div className="dark relative hidden md:block">
        <div className="absolute inset-0 h-full w-full" data-motion-media>
          <img
            src={where?.image?.url}
            alt={where?.image?.alt || ''}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <Spacer variant="xxl" />
        <Container className="relative z-10">
          <Grid>
            <div className="col-span-4 flex flex-col gap-spacing-lg">
              <Typography variant="h1" component="h2" data-motion-title>
                {where?.title}
              </Typography>
              <Typography
                variant="regular"
                className="text-text-primary-dark"
                data-motion-text
              >
                {where?.text}
              </Typography>
              <CtaButton
                href={where?.link1?.url}
                target={where?.link1?.target}
                variant="primary"
                className="place-self-start"
                data-motion-cta
              >
                {where?.link1?.title}
              </CtaButton>
            </div>
          </Grid>
        </Container>
        <Spacer variant="xxl" />
      </div>
    )
  }

  return (
    <Motion animation={whereToFindUsRevealAnimation}>
      {(motionRef) => (
        <div ref={motionRef} className={className} data-where-to-find-us>
          {renderMobile()}
          {type === 'small' ? renderDesktopSmall() : renderDesktopBig()}
        </div>
      )}
    </Motion>
  )
}

export default WhereToFindUs
